import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { Palette, Search } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function StudentWorks({ theme, toggleTheme }) {
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'student_works'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setWorks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const filtered = works.filter(w =>
    !searchTerm ||
    w.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.courseName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Navbar theme={theme} toggleTheme={toggleTheme} />
      <div className="min-h-screen flex flex-col pt-5 pb-16" dir="rtl">
        <div className="container mx-auto px-4 max-w-7xl animate-in fade-in duration-700 space-y-10 relative">

          {/* Ambient Grid Background */}
          <div className="fixed inset-0 pointer-events-none bg-[url('https://transparenttextures.com/patterns/cubes.png')] opacity-[0.02] dark:opacity-5 mix-blend-overlay" />

          {/* Cinematic Header */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-[#0a0a0a] to-primary-950 p-10 md:p-14 border border-white/10 shadow-2xl shadow-primary-900/20 group">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-500/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute bottom-0 left-10 w-64 h-64 bg-amber-500/20 rounded-full blur-[80px] pointer-events-none" />

            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/10 shadow-inner">
                  <Palette className="w-7 h-7 text-primary-400" />
                </div>
                <h1 className="text-4xl md:text-5xl font-black text-white drop-shadow-md tracking-tight">أعمال مبدعي لباب</h1>
              </div>
              <p className="text-zinc-400 text-lg md:text-xl font-medium max-w-2xl leading-relaxed mt-2">
                إبداعات طلابنا المتميزين الذين صنعوا الفارق من خلال دورات لباب سكول الاحترافية.
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative w-full lg:w-1/2 mx-auto group">
            <div className="absolute inset-y-0 right-0 pr-5 flex items-center pointer-events-none">
              <Search className="w-5 h-5 text-slate-400 dark:text-zinc-500 group-focus-within:text-primary-500 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="ابحث باسم الطالب أو الدورة..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-5 pr-14 py-4 bg-white/70 dark:bg-zinc-900/50 backdrop-blur-xl border border-slate-200/50 dark:border-white/5 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white font-medium transition-all shadow-sm"
            />
          </div>

          {/* Gallery Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-square rounded-3xl bg-zinc-200 dark:bg-zinc-800 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-slate-500 bg-white/50 dark:bg-zinc-900/40 backdrop-blur-2xl rounded-3xl border border-slate-200/50 dark:border-white/5 shadow-inner">
              <Palette className="w-16 h-16 text-slate-300 dark:text-zinc-700 mb-4" />
              <p className="text-2xl font-black text-slate-700 dark:text-white mb-2">لا توجد أعمال</p>
              <p className="text-slate-500 dark:text-zinc-400 font-medium">لم يتم إضافة أعمال بعد أو لا توجد نتائج مطابقة.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 relative z-10">
              {filtered.map((work, index) => (
                <div
                  key={work.id}
                  className="group w-[100%] relative rounded-3xl overflow-hidden border border-slate-200 dark:border-white/10 bg-white dark:bg-zinc-900 shadow-sm hover:shadow-2xl hover:shadow-primary-500/10 hover:-translate-y-1 transition-all duration-500"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  {/* Image */}
                  <div className="relative w-full aspect-video overflow-hidden">
                    <img
                      src={work.imageUrl}
                      alt={work.studentName}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>

                  {/* Info */}
                  <div className="p-5 space-y-1.5">
                    <h3 className="text-lg font-black text-slate-900 dark:text-white truncate">{work.studentName}</h3>
                    <p className="text-sm font-medium text-primary-600 dark:text-primary-400 truncate">{work.courseName}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
